import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  MessageInput,
  MessageInputProps,
  MessageList,
  MessageListProps,
  useChannelActionContext,
  useChannelStateContext,
  useMessageComposer,
} from 'stream-chat-react';

import { ChannelStatusIndicators } from 'src/components/chat/inner-channel/channel-content/ChannelStatusIndicators';
import { useDroppedFileContext } from 'src/components/chat/inner-channel/custom-message-input/DroppedFileContext';
import { ErrorText } from 'src/components/ErrorText';
import { useAssistantContext } from 'src/context';
import { useOSDetector } from 'src/hooks/useOSDetector';
// TODO in POR-15598: move MessageContent out of the legacy directory
// eslint-disable-next-line no-restricted-imports
import { MessageContent } from 'src/legacy/components/Chat/MessageContent';

export const MAX_CHARACTERS_LIMIT = 5000;
export const MAX_CHARACTERS_LIMIT_ERROR =
  'You have reached the 5000 character limit';

interface MessageListComponentProps extends MessageListProps {
  suppressAutoScroll?: boolean;
}
const MessageListComponent = (props: MessageListComponentProps) => {
  return <MessageList {...props} />;
};

// This is wrapper around MessageInput to allow for the textareaRef to be passed in.
// The MessageInputProps from stream-chat-react does not include textareaRef.
// but it does accept the prop. So we need to create a wrapper to allow for it
// to be passed in.
interface MessageInputComponentProps extends MessageInputProps {
  textareaRef?: MutableRefObject<HTMLTextAreaElement | undefined>;
}
const MessageInputComponent = (props: MessageInputComponentProps) => {
  return <MessageInput {...props} />;
};

export const ChannelContent = ({
  ChannelToolbar,
  MessageListOverride,
  autoFocus = true,
  defaultInputValue = '',
  onChange,
  onSubmit,
  showMessageInput = true,
  placeholderText,
  messageId,
  aiAssistantStatus,
  typingStatus,
  isInternalChat = false,
  setAIAssistantStatus,
  clearAIAssistantStatus,
  getCompanyFromId,
  getClientFromId,
}: {
  ChannelToolbar?: ReactNode;
  MessageListOverride?: ReactNode;
  autoFocus?: boolean;
  defaultInputValue?: string;
  onChange?: (text: string) => void;
  onSubmit?: () => void;
  showMessageInput?: boolean;
  placeholderText?: string;
  messageId?: string;
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
  } | null;
  typingStatus?: {
    typingUsers: Array<{
      user?: {
        id?: string;
        name?: string;
      };
    }>;
  } | null;
  isInternalChat?: boolean;
  setAIAssistantStatus?: (
    channelId: string,
    status: string,
    metadata?: { tool?: string; query?: string },
  ) => void;
  clearAIAssistantStatus?: () => void;
  getCompanyFromId?: (companyId: string) => { name?: string } | null;
  getClientFromId?: (clientId: string) => { name?: string } | null;
}) => {
  const isFirstRender = useRef(true);
  const messageInputRef = useRef<HTMLTextAreaElement>();
  const [hasMaxLimitReached, setHasMaxLimitReached] = useState(false);

  const messageComposer = useMessageComposer();
  const { channel } = useChannelStateContext();
  const { droppedFiles, clearDroppedFiles } = useDroppedFileContext();

  const { setTextareaRef, setTextComposer } = useAssistantContext();

  useEffect(() => {
    if (!isInternalChat) {
      setTextareaRef(messageInputRef);
      setTextComposer(messageComposer.textComposer);
    }
    return () => {
      setTextareaRef(null);
      setTextComposer(null);
    };
  }, [
    setTextareaRef,
    setTextComposer,
    messageComposer.textComposer,
    isInternalChat,
  ]);

  const { sendMessage, jumpToMessage } = useChannelActionContext();
  useEffect(() => {
    if (messageId && jumpToMessage) {
      jumpToMessage(messageId);
    }
  }, [messageId, jumpToMessage]);

  /**
   * This keyboard event listener handles focus the chat input
   * when ctrl & / keys are pressed.
   * @param event
   */
  const { isOSMetaKeyPressed } = useOSDetector();
  const onKeyDownHandler = (event: KeyboardEvent) => {
    if (isOSMetaKeyPressed(event)) {
      if (event.key === '/') {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }
    }
  };

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDownHandler, false);
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler, false);
    };
  }, []);

  // register onChange handler for the message composer
  useEffect(() => {
    if (onChange) {
      messageComposer.textComposer.middlewareExecutor.use([
        {
          id: 'input-state-handler',
          handlers: {
            onChange: async ({ state, next }) => {
              onChange?.(state.text);
              return next(state);
            },
            // we don't need to handle the suggestion item select
            // but since this is a required prop we need to define it
            // in the middleware executor
            onSuggestionItemSelect: async ({ state, next }) => {
              return next(state);
            },
          },
        },
      ]);
    }

    return () => {
      messageComposer.textComposer.middlewareExecutor.remove(
        'input-state-handler',
      );
    };
  }, [onChange, messageComposer.textComposer.middlewareExecutor]);

  const defaultInputValueRef = useRef(defaultInputValue);
  // Update textarea value when inputValue changes
  useEffect(() => {
    if (defaultInputValueRef.current && messageComposer.textComposer) {
      // Set the value and trigger both input and change events
      messageComposer.textComposer.setText(defaultInputValueRef.current);

      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }
  }, [messageComposer.textComposer]);

  /**
   * This method handles the chat input key up event.
   * 1. It checks if the message input has reached the max character limit.
   * 2. It allows the user to blur the input by pressing the escape key.
   */
  const handleInputKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.currentTarget.value.length >= MAX_CHARACTERS_LIMIT) {
        setHasMaxLimitReached(true);
      } else {
        setHasMaxLimitReached(false);
      }

      if (event.key === 'Escape') {
        messageInputRef.current?.blur();
      }
    },
    [],
  );

  return (
    <>
      {ChannelToolbar}
      {MessageListOverride ? (
        MessageListOverride
      ) : (
        <MessageListComponent
          Message={MessageContent}
          suppressAutoScroll={false}
        />
      )}
      {/* Unified status indicators: AI assistant status and typing indicators */}
      <ChannelStatusIndicators
        aiAssistantStatus={aiAssistantStatus}
        typingStatus={typingStatus}
        isInternalChat={isInternalChat}
        getCompanyFromId={getCompanyFromId}
        getClientFromId={getClientFromId}
        clearAIAssistantStatus={clearAIAssistantStatus}
      />
      {showMessageInput && (
        <>
          {hasMaxLimitReached && (
            <div className="px-4">
              <ErrorText>{MAX_CHARACTERS_LIMIT_ERROR}</ErrorText>
            </div>
          )}
          <MessageInputComponent
            // use textAreaRef prop to reference the chatAutoComplete component
            // passing the ref to the additionalTextAreaProp object causes
            // submitting message with enter key to not work.
            textareaRef={messageInputRef}
            maxRows={10}
            // focus input prop does not work when passing ref to the textarea
            // using textareaRef prop. This is a workaround to make it work.
            additionalTextareaProps={{
              autoFocus: isFirstRender.current && autoFocus,
              onKeyUp: handleInputKeyUp,
              // stream chat max character limit is 5000 by default
              // Here we are making sure is not able to type/paste more than 5000 characters in the input
              maxLength: MAX_CHARACTERS_LIMIT,
              placeholder: placeholderText,
            }}
            overrideSubmitHandler={async (params) => {
              if (onSubmit) {
                onSubmit();
              }

              // Add dropped files as custom attachments
              if (droppedFiles.length > 0) {
                params.message.attachments = params.message.attachments || [];

                droppedFiles.forEach((fileInfo) => {
                  params.message.attachments!.push({
                    type: 'file_id',
                    fields: [
                      {
                        title: 'file_id',
                        value: fileInfo.fileId,
                      },
                    ],
                  });
                });
              }

              // Set optimistic "sending" status for AI assistant only when AI is mentioned
              const channelId = channel?.cid;
              const messageText = params.localMessage?.text || '';
              const isAIMentioned = messageText.includes('@Assembly');

              // reset hasMaxLimitReached state
              setHasMaxLimitReached(false);
              try {
                await sendMessage(params);

                // Clear dropped files after successful send
                if (droppedFiles.length > 0) {
                  clearDroppedFiles();
                }

                if (
                  channelId &&
                  isInternalChat &&
                  isAIMentioned &&
                  setAIAssistantStatus
                ) {
                  setAIAssistantStatus(channelId, 'sending', undefined);
                }
              } catch (error) {
                if (isInternalChat && isAIMentioned && clearAIAssistantStatus) {
                  clearAIAssistantStatus();
                }
              }
            }}
          />
        </>
      )}
    </>
  );
};
